import type { GlycerinProductClickPayload } from '@packages/tracking';
import { GlycerinPlacement } from '@packages/tracking';
import type { FragmentType } from '@packages/gql/generated/shopping';
import { getFragmentData, graphql } from '@packages/gql/generated/shopping';

import type { ProductCardProps } from '@packages/modules/src/ProductCard/types';

export const SearchProductCardGlycerinTrackingResultFragment = graphql(/* GraphQL */ `
  fragment SearchProductCardGlycerinTrackingResultFragment on SearchProductResult {
    categories {
      name
      isSelected
    }
  }
`);

export const SearchProductCardGlycerinTrackingItemFragment = graphql(/* GraphQL */ `
  fragment SearchProductCardGlycerinTrackingItemFragment on StylePreviewItem {
    styleId
    styleName
    brand {
      name
    }
    listPosition
  }
`);

export const SearchProductCardGlycerinTrackingVariationGroupFragment = graphql(/* GraphQL */ `
  fragment SearchProductCardGlycerinTrackingVariationGroupFragment on CompleteVariationGroup {
    sku
  }
`);

export const buildGlycerinTrackingData = (
  maskedResult: FragmentType<typeof SearchProductCardGlycerinTrackingResultFragment>,
  maskedItem: FragmentType<typeof SearchProductCardGlycerinTrackingItemFragment>,
  maskedVariationGroup: FragmentType<
    typeof SearchProductCardGlycerinTrackingVariationGroupFragment
  >,
) => {
  const {
    styleId,
    brand: { name: brand },
    styleName: title,
    listPosition,
  } = getFragmentData(SearchProductCardGlycerinTrackingItemFragment, maskedItem);
  const { sku } = getFragmentData(
    SearchProductCardGlycerinTrackingVariationGroupFragment,
    maskedVariationGroup,
  );
  const { categories } = getFragmentData(
    SearchProductCardGlycerinTrackingResultFragment,
    maskedResult,
  );

  const currentCategoryName = categories.find((category) => category.isSelected)?.name;

  const listId = currentCategoryName || 'Search Result';

  // primaryVariationGroup.articleNumber is missing the promo code, so we need to extract it from the sku
  const articleNumberWithPromo = sku.split('-')[0];

  const clickTrackingProps: GlycerinProductClickPayload = {
    category: 'product-click',
    productId: styleId,
    articleId: articleNumberWithPromo,
    skuId: sku,
    detail: `${brand} ${title}`,
    listPosition,
    listId,
    placement: GlycerinPlacement.LISTING,
  };

  const wishListTrackingProduct: NonNullable<ProductCardProps['wishListTrackingProps']>['product'] =
    {
      productId: styleId,
      articleId: articleNumberWithPromo,
      // SKU is already automatically passed to the wishlist button for tracking from ProductCard, so no need to pass it here
      listPosition,
      origin: listId,
    };

  return {
    clickTrackingProps,
    wishListTrackingProduct,
  };
};
